// PHA-567 / PHA-575: drawer that opens when an AI insight card is clicked.
// Holds: CDS framing banner, why-now headline, trend sparklines + narratives,
// MA action, MD action (when present), citation chips, feedback affordances,
// and a "Refresh" button that recomputes on demand.

import React, { useCallback, useState } from 'react';
import { Box, Button, Card, Dialog, Flex, Heading, Text, Badge, IconButton, TextArea } from '@radix-ui/themes';
import { Cross1Icon, ReloadIcon, ThickArrowUpIcon, ThickArrowDownIcon } from '@radix-ui/react-icons';
import { aiInsightsApi } from '../../../services/api';
import type { PatientAiInsight } from '../types';
import TrendSparkline from './TrendSparkline';
import ActionBlock from './ActionBlock';
import CitationLink from './CitationLink';

interface AiInsightDrawerProps {
  insight: PatientAiInsight | null;
  organizationId?: string;
  open: boolean;
  onClose: () => void;
  onRefreshed?: (next: PatientAiInsight) => void;
}

const tierColor = (tier: PatientAiInsight['risk_tier']): 'red' | 'orange' | 'green' => {
  if (tier === 'critical') return 'red';
  if (tier === 'watch') return 'orange';
  return 'green';
};

const formatComputedAt = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const AiInsightDrawer: React.FC<AiInsightDrawerProps> = ({
  insight,
  organizationId,
  open,
  onClose,
  onRefreshed,
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [feedbackPosting, setFeedbackPosting] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState<string | null>(null);
  const [showWrongReason, setShowWrongReason] = useState(false);
  const [wrongReason, setWrongReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleRefresh = useCallback(async () => {
    if (!insight) return;
    setRefreshing(true);
    setError(null);
    try {
      const { status, data } = await aiInsightsApi.refresh(insight.patient_id, organizationId);
      if (status === 429) {
        setError(`Rate limited — try again in ${data?.retry_after_seconds ?? 30}s`);
      } else if (status >= 400) {
        setError(data?.error || 'Refresh failed');
      } else if (data?.insight && onRefreshed) {
        onRefreshed(data.insight);
      }
    } catch (err) {
      setError((err as Error).message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }, [insight, organizationId, onRefreshed]);

  const sendFeedback = useCallback(
    async (rating: 'useful' | 'not_useful' | 'wrong', reason?: string) => {
      if (!insight) return;
      setFeedbackPosting(rating);
      try {
        await aiInsightsApi.feedback(insight.insight_id, { rating, reason });
        setFeedbackSent(rating);
        setShowWrongReason(false);
      } catch (err) {
        setError((err as Error).message || 'Feedback failed');
      } finally {
        setFeedbackPosting(null);
      }
    },
    [insight],
  );

  if (!insight) return null;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Content style={{ maxWidth: 560 }} aria-describedby={undefined}>
        <Flex direction="column" gap="3">
          <Flex align="center" justify="between" gap="2">
            <Flex align="center" gap="2">
              <Badge color={tierColor(insight.risk_tier)} variant="solid" size="2">
                {insight.risk_tier.toUpperCase()}
              </Badge>
              <Heading size="3">{insight.patient_name || insight.patient_id}</Heading>
              {insight.live_signal && (
                <Badge color="red" variant="soft" size="1" title="Live reading after last AI computation">
                  ● LIVE
                </Badge>
              )}
              <Badge color="gray" variant="soft" size="1">Beta</Badge>
            </Flex>
            <IconButton variant="ghost" onClick={onClose} aria-label="Close">
              <Cross1Icon />
            </IconButton>
          </Flex>

          {/* PHA-575 CDS framing — persistent banner */}
          <Card variant="surface" style={{ background: 'var(--amber-2)' }}>
            <Text size="1">
              <strong>Suggested — review before action.</strong> Not a diagnosis.
              Computed {formatComputedAt(insight.computed_at)} • prompt {insight.prompt_version}
            </Text>
          </Card>

          <Text size="3" weight="medium">{insight.why_now}</Text>

          {insight.trend_callouts.length > 0 && (
            <Card variant="classic">
              <Heading size="2" mb="2">Trends</Heading>
              <Flex direction="column" gap="2">
                {insight.trend_callouts.map((t) => {
                  const points = insight.sparkline_series[t.sparkline_series_id] || [];
                  const arrow =
                    t.direction === 'rising' ? <ThickArrowUpIcon /> :
                    t.direction === 'falling' ? <ThickArrowDownIcon /> : null;
                  return (
                    <Flex key={t.sparkline_series_id} align="center" gap="3">
                      <Box style={{ width: 60, fontSize: 12, color: 'var(--gray-11)' }}>
                        {t.metric.toUpperCase()}
                      </Box>
                      <TrendSparkline points={points} ariaLabel={`${t.metric} ${t.direction}`} />
                      <Text size="2" style={{ flex: 1 }}>
                        {arrow}
                        {' '}{t.narrative}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            </Card>
          )}

          <ActionBlock title="Recommended Action" action={insight.ma_action} variant="ma" />
          {insight.md_action && (
            <ActionBlock title="What the physician should consider" action={insight.md_action} variant="md" />
          )}

          {insight.citations.length > 0 && (
            <Box>
              <Text size="1" color="gray" weight="bold">Citations — click to open in chart</Text>
              <Flex gap="2" wrap="wrap" mt="2">
                {insight.citations.map((c, idx) => (
                  <CitationLink key={idx} citation={c} patientId={insight.patient_id} />
                ))}
              </Flex>
            </Box>
          )}

          {error && <Text size="1" color="red">{error}</Text>}

          <Flex justify="between" align="center" gap="2" wrap="wrap">
            <Button variant="soft" onClick={handleRefresh} disabled={refreshing}>
              <ReloadIcon /> {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
            {feedbackSent ? (
              <Text size="1" color="gray">Feedback recorded — thank you.</Text>
            ) : (
              <Flex gap="2" align="center">
                <Button
                  variant="soft"
                  color="green"
                  disabled={feedbackPosting !== null}
                  onClick={() => sendFeedback('useful')}
                >
                  👍 Useful
                </Button>
                <Button
                  variant="soft"
                  color="orange"
                  disabled={feedbackPosting !== null}
                  onClick={() => sendFeedback('not_useful')}
                >
                  👎 Not useful
                </Button>
                <Button
                  variant="soft"
                  color="red"
                  disabled={feedbackPosting !== null}
                  onClick={() => setShowWrongReason((s) => !s)}
                >
                  🚫 Wrong
                </Button>
              </Flex>
            )}
          </Flex>
          {showWrongReason && (
            <Flex direction="column" gap="2">
              <TextArea
                placeholder="What did the insight get wrong? (required)"
                value={wrongReason}
                onChange={(e) => setWrongReason(e.target.value)}
              />
              <Button
                disabled={!wrongReason.trim() || feedbackPosting !== null}
                onClick={() => sendFeedback('wrong', wrongReason.trim())}
              >
                Submit
              </Button>
            </Flex>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AiInsightDrawer;
